import React, { useState, useMemo, useEffect } from "react";
import axios from "../http"; // axios instance that attaches the JWT
import Select from "react-select";
import Layout from "../Component/Layout";
import { TILE_REGISTRY } from "../Component/Favourite/TileRegistry";
import { getFieldsForModule } from "./FieldRegistry";
import { MASTER_API } from "../apiConfig";

// Pick these in the Module dropdown instead of a normal module.
//  Full Access -> every module, including User Management
//  No Access   -> can log in only
// Each one replaces everything else in the role (they can't be mixed).
const ACCESS_PRESETS = {
  FULL_ACCESS: "Full Access",
  NO_ACCESS: "No Access"
};

// Every access type a module can carry. "Full Access" on a module simply
// adds all five of these for it.
const ALL_TYPES = ["CREATE", "DISPLAY", "CHANGE", "REVIEW", "CONVERT"];
// Only these two have a per-field checklist.
const FIELD_TYPES = ["DISPLAY", "CHANGE"];

// Plain on/off grants: no access type to choose (always saved as DISPLAY).
const isLockedModule = (id) => id === "USER_MANAGEMENT" || !!ACCESS_PRESETS[id];

const ROLE_TYPE_LABELS = {
  CREATE: "Create",
  DISPLAY: "Display",
  CHANGE: "Change",
  REVIEW: "Review",
  CONVERT: "Convert"
};

// react-select styles matching the plain <select> look used elsewhere
// (styles.select), plus a portal so the menu isn't clipped by the
// scrollable sidebar/card containers — same fix used on GRN/Gate Pass/PO dropdowns.
const rsSelectStyles = {
  control: (base, state) => ({
    ...base,
    minWidth: 220,
    borderRadius: 6,
    borderColor: "#cbd5e1",
    boxShadow: "none",
    fontSize: 13.5,
    "&:hover": { borderColor: "#cbd5e1" }
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  groupHeading: (base) => ({
    ...base,
    fontSize: 11.5,
    fontWeight: 700,
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: 0.3
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 13.5,
    backgroundColor: state.isSelected
      ? "#2563eb"
      : state.isFocused
      ? "#eff6ff"
      : "#fff",
    color: state.isSelected ? "#fff" : "#1e293b"
  })
};

const Role = () => {
  // All tiles grouped by moduleName, for the <optgroup> dropdown.
  const groupedModules = useMemo(() => {
    const groups = {};
    Object.values(TILE_REGISTRY).forEach((tile) => {
      if (!groups[tile.moduleName]) groups[tile.moduleName] = [];
      groups[tile.moduleName].push(tile);
    });
    return groups;
  }, []);

  const [roleName, setRoleName] = useState("");

  // Modules added to this role. Keyed by `${module_id}::${permission_type}`
  // (not just module_id) so the same module can appear more than once with
  // different access types — e.g. Enquiry as both Display and Create.
  // Value: { module_id, permission_type, fields: { [field_id]: bool } }
  const [addedModules, setAddedModules] = useState({});

  // ---- "add a module" staging area (one at a time) ----
  const [pendingModuleIds, setPendingModuleIds] = useState([]); // Module dropdown is multi-select
  // Field checklists only make sense for ONE module; with several selected
  // the types apply to each module with every field on.
  const pendingModuleId = pendingModuleIds.length === 1 ? pendingModuleIds[0] : "";
  const typesLocked = pendingModuleIds.length > 0 && pendingModuleIds.every(isLockedModule);
  const [pendingTypes, setPendingTypes] = useState([]); // e.g. ["DISPLAY","CHANGE"] or ["FULL"]
  const [pendingFields, setPendingFields] = useState({}); // { DISPLAY: {field_id: bool}, CHANGE: {...} }

  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  // ---- saved roles list (fetched from the backend, shown in a table below) ----
  const [savedRoles, setSavedRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState(null); // null = creating a new role

  const fetchRoles = async () => {
    try {
      setLoadingRoles(true);
      const res = await axios.get(`${MASTER_API}/roles`);
      setSavedRoles(res.data || []);
    } catch (err) {
      console.error("FETCH ROLES ERROR:", err);
    } finally {
      setLoadingRoles(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const availableFields = pendingModuleId ? getFieldsForModule(pendingModuleId) : [];

  const entryKey = (moduleId, type) => `${moduleId}::${type}`;

  const resetPending = () => {
    setPendingModuleIds([]);
    setPendingTypes([]);
    setPendingFields({});
  };

  const defaultFieldsFor = (moduleId) => {
    const fields = getFieldsForModule(moduleId);
    const defaults = {};
    fields.forEach((f) => {
      defaults[f.field_id] = true;
    });
    return defaults;
  };

  // Module is multi-select. Full Access / No Access presets stand alone:
  // picking one clears the rest, picking a normal module clears the preset.
  const handleModulesChange = (opts) => {
    let ids = (opts || []).map((o) => o.value);
    const newPreset = ids.find((id) => ACCESS_PRESETS[id] && !pendingModuleIds.includes(id));
    ids = newPreset ? [newPreset] : ids.filter((id) => !ACCESS_PRESETS[id]);

    const lockedOnly = ids.length > 0 && ids.every(isLockedModule);
    const types = lockedOnly ? ["DISPLAY"] : typesLocked ? [] : pendingTypes;

    const nextFields = {};
    if (ids.length === 1 && !lockedOnly) {
      types.forEach((t) => {
        if (!FIELD_TYPES.includes(t)) return;
        nextFields[t] = addedModules[entryKey(ids[0], t)]?.fields || defaultFieldsFor(ids[0]);
      });
    }

    setPendingModuleIds(ids);
    setPendingTypes(types);
    setPendingFields(nextFields);
  };

  // Role Type is multi-select. "FULL" (Full Access for this module) stands
  // alone: picking it clears the others, picking another type clears it.
  const handleTypesChange = (opts) => {
    let types = (opts || []).map((o) => o.value);
    if (types.includes("FULL") && !pendingTypes.includes("FULL")) {
      types = ["FULL"];
    } else {
      types = types.filter((t) => t !== "FULL");
    }

    // Each field-based type keeps its own checklist: reuse what's staged,
    // else what's already added to the role, else everything checked.
    const nextFields = {};
    types.forEach((t) => {
      if (!FIELD_TYPES.includes(t)) return;
      nextFields[t] =
        pendingFields[t] ||
        addedModules[entryKey(pendingModuleId, t)]?.fields ||
        (pendingModuleId ? defaultFieldsFor(pendingModuleId) : {});
    });

    setPendingTypes(types);
    setPendingFields(nextFields);
  };

  const toggleFieldVisible = (type, fieldId) => {
    setPendingFields((prev) => ({
      ...prev,
      [type]: { ...(prev[type] || {}), [fieldId]: !prev[type]?.[fieldId] }
    }));
  };

  // Select all / Deselect all for one type's field checklist.
  const setAllPendingFields = (type, value) => {
    const next = {};
    availableFields.forEach((f) => {
      next[f.field_id] = value;
    });
    setPendingFields((prev) => ({ ...prev, [type]: next }));
  };

  const allCheckedFor = (type) =>
    availableFields.length > 0 && availableFields.every((f) => !!pendingFields[type]?.[f.field_id]);

  const addDisabled = pendingModuleIds.length === 0 || (!typesLocked && pendingTypes.length === 0);

  const handleAddModule = () => {
    if (addDisabled) return;

    const isFull = pendingTypes.includes("FULL");
    const hasPreset = pendingModuleIds.some((id) => ACCESS_PRESETS[id]);
    const single = pendingModuleIds.length === 1;

    setAddedModules((prev) => {
      // Presets stand alone: choosing one clears the rest, and adding
      // normal modules later clears a preset.
      const base = hasPreset
        ? {}
        : Object.fromEntries(
            Object.entries(prev).filter(([, cfg]) => !ACCESS_PRESETS[cfg.module_id])
          );

      const next = { ...base };
      pendingModuleIds.forEach((moduleId) => {
        const locked = isLockedModule(moduleId);
        const types = locked ? ["DISPLAY"] : isFull ? ALL_TYPES : pendingTypes;

        types.forEach((type) => {
          const key = entryKey(moduleId, type);
          const hasFields = !locked && FIELD_TYPES.includes(type);

          let fields = {};
          if (hasFields) {
            if (isFull) {
              fields = defaultFieldsFor(moduleId);
            } else if (single) {
              fields = { ...(pendingFields[type] || {}) };
            } else {
              // several modules at once: keep an existing config, else all fields on
              const existing = prev[key]?.fields;
              fields = existing && Object.keys(existing).length ? existing : defaultFieldsFor(moduleId);
            }
          }

          next[key] = { module_id: moduleId, permission_type: type, fields };
        });
      });
      return next;
    });

    resetPending();
  };

  const handleRemoveModule = (key) => {
    setAddedModules((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleEditModule = (key) => {
    const cfg = addedModules[key];
    if (!cfg) return;
    setPendingModuleIds([cfg.module_id]);
    setPendingTypes([cfg.permission_type]);
    setPendingFields({ [cfg.permission_type]: cfg.fields || {} });
  };

  const handleSave = async () => {
    if (!roleName.trim()) {
      alert("Enter a role name");
      return;
    }

    const entries = Object.values(addedModules);
    if (entries.length === 0) {
      alert("Add at least one module to this role");
      return;
    }

    const payloadModules = entries.map((cfg) => {
      // is_visible meaning depends on permission_type:
      //  DISPLAY -> 1 = value shown normally, 0 = label shows, value masked/blurred.
      //  CREATE  -> 1 = field appears on the create form, 0 = field left out entirely.
      const fields = Object.entries(cfg.fields || {}).map(([field_id, is_visible]) => ({
        field_id,
        is_visible: is_visible ? 1 : 0
      }));

      return { module_id: cfg.module_id, permission_type: cfg.permission_type, fields };
    });

    try {
      setSaving(true);
      setMessage("");

      if (editingRoleId) {
        await axios.put(`${MASTER_API}/roles/${editingRoleId}`, {
          role_name: roleName.trim(),
          modules: payloadModules
        });
        setMessage(`✅ Role "${roleName.trim()}" updated successfully`);
      } else {
        await axios.post(`${MASTER_API}/roles`, {
          role_name: roleName.trim(),
          modules: payloadModules
        });
        setMessage(`✅ Role "${roleName.trim()}" created successfully`);
      }

      setRoleName("");
      setAddedModules({});
      setEditingRoleId(null);
      resetPending();
      fetchRoles();
    } catch (err) {
      console.error(editingRoleId ? "UPDATE ROLE ERROR:" : "CREATE ROLE ERROR:", err);
      setMessage(`❌ ${err.response?.data?.detail || err.response?.data?.message || (editingRoleId ? "Error updating role" : "Error creating role")}`);
    } finally {
      setSaving(false);
    }
  };

  // Pulls a previously-saved role back into the builder so its modules can
  // be changed and re-saved. Fetches the full record (with fields) since
  // the roles list endpoint only returns the summary columns.
  const handleEditSavedRole = async (roleId) => {
    try {
      setMessage("");
      const res = await axios.get(`${MASTER_API}/roles/${roleId}`);
      const role = res.data;

      const modulesObj = {};
      (role.modules || []).forEach((m) => {
        const fieldsObj = {};
        (m.fields || []).forEach((f) => {
          fieldsObj[f.field_id] = !!f.is_visible;
        });
        modulesObj[entryKey(m.module_id, m.permission_type)] = {
          module_id: m.module_id,
          permission_type: m.permission_type,
          fields: fieldsObj
        };
      });

      setRoleName(role.role_name || "");
      setAddedModules(modulesObj);
      setEditingRoleId(role.role_id);
      resetPending();
    } catch (err) {
      console.error("LOAD ROLE FOR EDIT ERROR:", err);
      setMessage("❌ Error loading role for edit");
    }
  };

  const handleCancelEdit = () => {
    setEditingRoleId(null);
    setRoleName("");
    setAddedModules({});
    resetPending();
    setMessage("");
  };

  // Deletes a role from the database. If it's still assigned to users the
  // server answers 409 with a count, and we ask a second time before
  // retrying with force so nobody loses access by accident.
  const handleDeleteRole = async (role, force = false) => {
    if (!force && !window.confirm(`Delete role "${role.role_name}"? This can't be undone.`)) {
      return;
    }

    try {
      await axios.delete(
        `${MASTER_API}/roles/${role.role_id}`,
        force ? { params: { force: true } } : undefined
      );

      if (editingRoleId === role.role_id) handleCancelEdit();
      setMessage(`✅ Role "${role.role_name}" deleted`);
      fetchRoles();
    } catch (err) {
      const data = err.response?.data;

      if (err.response?.status === 409 && data?.assigned_count && !force) {
        const ok = window.confirm(
          `"${role.role_name}" is assigned to ${data.assigned_count} user email(s). ` +
            `Deleting it removes that access from them. Delete anyway?`
        );
        if (ok) handleDeleteRole(role, true);
        return;
      }

      console.error("DELETE ROLE ERROR:", err);
      setMessage(`❌ ${data?.detail || data?.message || (err.response ? `Error deleting role (HTTP ${err.response.status})` : "Couldn't reach the server — network or CORS error (see browser console)")}`);
    }
  };

  const addedEntries = Object.entries(addedModules); // [key, cfg][]

  // How many access types are already added per module_id, just for the
  // dropdown hint (e.g. "Enquiry (2 access types added)").
  const addedCountByModule = addedEntries.reduce((acc, [, cfg]) => {
    acc[cfg.module_id] = (acc[cfg.module_id] || 0) + 1;
    return acc;
  }, {});

  // react-select grouped options, built from the same groupedModules map
  // used for the old <optgroup> list.
  const presetGroup = {
    label: "Access Presets",
    options: [
      { value: "FULL_ACCESS", label: "Full Access (all modules + User Management)" },
      { value: "NO_ACCESS", label: "No Access (login only)" }
    ]
  };

  const moduleSelectOptions = [presetGroup, ...Object.entries(groupedModules).map(([moduleName, tiles]) => ({
    label: moduleName,
    options: tiles.map((tile) => ({
      value: tile.id,
      label:
        tile.name +
        (addedCountByModule[tile.id]
          ? ` (${addedCountByModule[tile.id]} access type${addedCountByModule[tile.id] > 1 ? "s" : ""} added)`
          : "")
    }))
  }))];

  const findModuleOption = (moduleId) =>
    moduleSelectOptions.flatMap((group) => group.options).find((opt) => opt.value === moduleId) || null;

  const roleTypeOptions = [
    { value: "FULL", label: "★ Full Access (all types for this module)" },
    ...ALL_TYPES.map((type) => ({
      value: type,
      label:
        ROLE_TYPE_LABELS[type] +
        (addedModules[entryKey(pendingModuleId, type)] ? " (added — edit)" : "")
    }))
  ];

  const selectedTypeOptions = roleTypeOptions.filter((o) => pendingTypes.includes(o.value));

  const roleTypeSelectStyles = {
    ...rsSelectStyles,
    control: (base, state) => ({ ...rsSelectStyles.control(base, state), minWidth: 300 })
  };

  return (
    <Layout>
      <div style={styles.pageTitleBar}>
        <div>
          <div style={styles.pageTitle}>Create Role</div>
          <div style={styles.pageSubtitle}>
            Pick one or more modules and one or more access types (or Full Access), then add them to the role.
          </div>
        </div>
      </div>

      <div style={styles.card}>
        {editingRoleId && (
          <div style={styles.editingBanner}>
            <span>Editing role #{editingRoleId} — changes will overwrite this role when saved.</span>
            <button onClick={handleCancelEdit} style={styles.linkBtn}>
              Cancel edit
            </button>
          </div>
        )}

        <input
          placeholder="Role name (e.g. Sales Viewer)"
          value={roleName}
          onChange={(e) => setRoleName(e.target.value)}
          style={styles.input}
        />

        {/* ================= ADD MODULE ================= */}
        <div style={styles.addModuleCard}>
          <div style={styles.addModuleTitle}>Add Module Access</div>

          <div style={styles.addModuleRow}>
            <div style={styles.fieldGroup}>
              <label style={styles.smallLabel}>Module</label>
              <Select
                isMulti
                value={pendingModuleIds.map(findModuleOption).filter(Boolean)}
                onChange={handleModulesChange}
                options={moduleSelectOptions}
                placeholder="Select one or more modules…"
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                styles={roleTypeSelectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.smallLabel}>Role Type</label>
              <Select
                isMulti
                value={selectedTypeOptions}
                onChange={handleTypesChange}
                options={roleTypeOptions}
                placeholder="Select one or more…"
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                isDisabled={pendingModuleIds.length === 0 || typesLocked}
                styles={roleTypeSelectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <button
              onClick={handleAddModule}
              disabled={addDisabled}
              style={{
                ...styles.addBtn,
                opacity: addDisabled ? 0.5 : 1,
                cursor: addDisabled ? "not-allowed" : "pointer"
              }}
            >
              + Add to Role
            </button>
          </div>

          {pendingModuleIds.length > 1 && !typesLocked && (
            <p style={{ fontSize: 13, color: "#64748b", marginTop: 8 }}>
              {pendingModuleIds.length} modules selected: the chosen access types are added to each module with every field visible/editable. Select a single module if you want to pick fields.
            </p>
          )}

          {/* Create / Review / Convert have no per-field picker. */}
          {pendingModuleId && pendingTypes.includes("FULL") && (
            <p style={{ fontSize: 13, color: "#64748b", marginTop: 8 }}>
              Full access to {TILE_REGISTRY[pendingModuleId]?.name || pendingModuleId}: Create, Display, Change, Review and Convert, with every field visible and editable. Click "Add to Role".
            </p>
          )}
          {pendingModuleId && pendingTypes.includes("CREATE") && (
            <p style={{ fontSize: 13, color: "#64748b", marginTop: 8 }}>
              Create: this role can create a new {TILE_REGISTRY[pendingModuleId]?.name || pendingModuleId} record. No per-field configuration.
            </p>
          )}
          {pendingModuleId && pendingTypes.includes("REVIEW") && (
            <p style={{ fontSize: 13, color: "#64748b", marginTop: 8 }}>
              Review: this role only sees the Review tab for {TILE_REGISTRY[pendingModuleId]?.name || pendingModuleId} records — Basic, Product, Docs, and Change Log are hidden.
            </p>
          )}
          {pendingModuleId && pendingTypes.includes("CONVERT") && (
            <p style={{ fontSize: 13, color: "#64748b", marginTop: 8 }}>
              Convert: this role can convert {TILE_REGISTRY[pendingModuleId]?.name || pendingModuleId} records (e.g. Planned Order → Production Order). No per-field configuration.
            </p>
          )}

          {pendingModuleId === "FULL_ACCESS" && (
            <p style={{ fontSize: 13, color: "#64748b", marginTop: 8 }}>
              Gives access to every module, including User Management. Click "Add to Role" — this replaces any other modules in the role.
            </p>
          )}
          {pendingModuleId === "NO_ACCESS" && (
            <p style={{ fontSize: 13, color: "#64748b", marginTop: 8 }}>
              Users with this role can log in, but no module opens for them. Click "Add to Role" — this replaces any other modules in the role.
            </p>
          )}

          {pendingModuleId === "USER_MANAGEMENT" && (
            <p style={{ fontSize: 13, color: "#64748b", marginTop: 8 }}>
              Gives full access to User Management — Create Role and Create User. Click "Add to Role".
            </p>
          )}

          {/* ---- field checklist: one box per selected Display / Change ---- */}
          {pendingModuleId &&
            pendingModuleId !== "USER_MANAGEMENT" &&
            !ACCESS_PRESETS[pendingModuleId] &&
            FIELD_TYPES.filter((t) => pendingTypes.includes(t)).map((type) => (
              <div key={type} style={styles.fieldConfigBox}>
                <div style={styles.fieldConfigHeaderRow}>
                  <div style={styles.fieldConfigHeader}>
                    {ROLE_TYPE_LABELS[type]} — Field {type === "CHANGE" ? "Access" : "Visibility"} — {TILE_REGISTRY[pendingModuleId]?.name || pendingModuleId}
                  </div>
                  {availableFields.length > 0 && (
                    <div style={styles.selectAllGroup}>
                      <button
                        type="button"
                        onClick={() => setAllPendingFields(type, true)}
                        disabled={allCheckedFor(type)}
                        style={{
                          ...styles.selectAllBtn,
                          opacity: allCheckedFor(type) ? 0.5 : 1,
                          cursor: allCheckedFor(type) ? "not-allowed" : "pointer"
                        }}
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={() => setAllPendingFields(type, false)}
                        style={styles.selectAllBtn}
                      >
                        Deselect All
                      </button>
                    </div>
                  )}
                </div>
                <p style={styles.fieldConfigHint}>
                  {type === "CHANGE"
                    ? "Checked = field can be edited. Unchecked = field shows but is read-only in Edit mode."
                    : "Checked = field and its value are fully visible. Unchecked = field name still shows, but the value is blurred/masked."}
                </p>

                {availableFields.length === 0 ? (
                  <p style={{ fontSize: 13, color: "#94a3b8" }}>
                    No fields configured yet for this module. Add it in FieldRegistry.js.
                  </p>
                ) : (
                  <>
                    <div style={styles.fieldGrid}>
                      {availableFields
                        .filter((f) => !f.group)
                        .map((f) => (
                          <label key={f.field_id} style={styles.fieldCheckRow}>
                            <input
                              type="checkbox"
                              checked={!!pendingFields[type]?.[f.field_id]}
                              onChange={() => toggleFieldVisible(type, f.field_id)}
                            />
                            <span>{f.label}</span>
                          </label>
                        ))}
                    </div>

                    {Object.entries(
                      availableFields
                        .filter((f) => f.group)
                        .reduce((acc, f) => {
                          (acc[f.group] = acc[f.group] || []).push(f);
                          return acc;
                        }, {})
                    ).map(([groupName, groupFields]) => (
                      <div key={groupName} style={styles.fieldGroupSection}>
                        <div style={styles.fieldGroupHeader}>{groupName}</div>
                        <div style={styles.fieldGrid}>
                          {groupFields.map((f) => (
                            <label key={f.field_id} style={styles.fieldCheckRow}>
                              <input
                                type="checkbox"
                                checked={!!pendingFields[type]?.[f.field_id]}
                                onChange={() => toggleFieldVisible(type, f.field_id)}
                              />
                              <span>{f.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            ))}
        </div>

        {/* ================= CONFIGURED MODULES LIST ================= */}
        <div style={styles.listSection}>
          <div style={styles.listTitle}>
            Modules in this role {addedEntries.length > 0 && `(${addedEntries.length})`}
          </div>

          {addedEntries.length === 0 ? (
            <div style={styles.emptyState}>No modules added yet.</div>
          ) : (
            <table style={styles.erpTable}>
              <colgroup>
                <col style={{ width: "34%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "28%" }} />
                <col style={{ width: "20%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={styles.erpTh}>Module</th>
                  <th style={styles.erpTh}>Access Type</th>
                  <th style={styles.erpTh}>Field Config</th>
                  <th style={styles.erpTh}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {addedEntries.map(([key, cfg]) => {
                  const tile = TILE_REGISTRY[cfg.module_id];
                  const fieldCount = Object.keys(cfg.fields || {}).length;
                  const visibleCount = Object.values(cfg.fields || {}).filter(Boolean).length;

                  return (
                    <tr key={key}>
                      <td style={styles.erpTd}>{ACCESS_PRESETS[cfg.module_id] || tile?.name || cfg.module_id}</td>
                      <td style={styles.erpTd}>
                        {ACCESS_PRESETS[cfg.module_id]
                          ? "—"
                          : { CREATE: "Create", DISPLAY: "Display", CHANGE: "Change", REVIEW: "Review", CONVERT: "Convert" }[cfg.permission_type] || cfg.permission_type}
                      </td>
                      <td style={styles.erpTd}>
                        {fieldCount > 0
                          ? cfg.permission_type === "CHANGE"
                            ? `${visibleCount} of ${fieldCount} editable`
                            : `${visibleCount} of ${fieldCount} visible`
                          : "—"}
                      </td>
                      <td style={styles.erpTd}>
                        <button
                          onClick={() => handleEditModule(key)}
                          style={styles.linkBtn}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleRemoveModule(key)}
                          title="Remove"
                          style={styles.removeBtn}
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <button onClick={handleSave} disabled={saving} style={styles.primaryBtn}>
          {saving ? "Saving..." : editingRoleId ? "Update Role" : "Save Role"}
        </button>

        {message && <div style={styles.messageBox}>{message}</div>}
      </div>

      {/* ================= SAVED ROLES ================= */}
      <div style={{ ...styles.card, marginTop: 24 }}>
        <div style={styles.listTitle}>
          Saved Roles {savedRoles.length > 0 && `(${savedRoles.length})`}
        </div>

        {loadingRoles ? (
          <div style={styles.emptyState}>Loading roles…</div>
        ) : savedRoles.length === 0 ? (
          <div style={styles.emptyState}>No roles saved yet.</div>
        ) : (
          <table style={styles.erpTable}>
            <colgroup>
              <col style={{ width: "34%" }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: "24%" }} />
              <col style={{ width: "20%" }} />
            </colgroup>
            <thead>
              <tr>
                <th style={styles.erpTh}>Role Name</th>
                <th style={styles.erpTh}>Access Type</th>
                <th style={styles.erpTh}>Created At</th>
                <th style={styles.erpTh}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {savedRoles.map((role) => {
                const typeLabels = { CREATE: "Create", DISPLAY: "Display", CHANGE: "Change", REVIEW: "Review", CONVERT: "Convert" };
                const accessTypeText = (role.access_types || "")
                  .split(",")
                  .filter(Boolean)
                  .map((t) => typeLabels[t] || t)
                  .join(", ");

                return (
                  <tr key={role.role_id}>
                    <td style={styles.erpTd}>{role.role_name}</td>
                    <td style={styles.erpTd}>
                      {Number(role.is_full_access)
                        ? "Full Access"
                        : Number(role.is_no_access)
                        ? "No Access"
                        : accessTypeText || "—"}
                    </td>
                    <td style={styles.erpTd}>
                      {role.created_at ? new Date(role.created_at).toLocaleString() : "—"}
                    </td>
                    <td style={styles.erpTd}>
                      {role.role_name === FULL_ACCESS_ROLE_NAME ? (
                        <span style={{ fontSize: 12, color: "#94a3b8" }}>System role</span>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEditSavedRole(role.role_id)}
                            style={styles.linkBtn}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteRole(role)}
                            style={styles.dangerBtn}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
};

const FULL_ACCESS_ROLE_NAME = "Full Access (auto-generated)";

const styles = {
  pageTitleBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 24,
    flexWrap: "wrap",
    gap: 10
  },
  pageTitle: { fontSize: 26, fontWeight: 700, color: "#1e293b", marginBottom: 2 },
  pageSubtitle: { fontSize: 13, color: "#64748b" },
  card: {
    background: "#fff",
    borderRadius: 8,
    padding: 20,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)"
  },
  editingBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#fff7ed",
    border: "1px solid #fdba74",
    color: "#9a3412",
    borderRadius: 6,
    padding: "8px 14px",
    fontSize: 13,
    marginBottom: 16
  },
  input: {
    marginBottom: 20,
    padding: "10px",
    width: "320px",
    borderRadius: 5,
    border: "1px solid #ccc",
    fontSize: 14
  },
  addModuleCard: {
    background: "#f8fafc",
    border: "1px solid #e5e9f0",
    borderRadius: 8,
    padding: 18,
    marginBottom: 24
  },
  addModuleTitle: { fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 14 },
  addModuleRow: {
    display: "flex",
    gap: 16,
    alignItems: "flex-end",
    flexWrap: "wrap"
  },
  fieldGroup: { display: "flex", flexDirection: "column", minWidth: 220 },
  smallLabel: { fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 5 },
  select: {
    padding: "9px 10px",
    borderRadius: 6,
    border: "1px solid #cbd5e1",
    fontSize: 13.5,
    background: "#fff",
    minWidth: 220
  },
  addBtn: {
    padding: "10px 18px",
    background: "#2563eb",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    fontSize: 13.5,
    fontWeight: 600,
    height: 38
  },
  fieldConfigBox: {
    marginTop: 18,
    paddingTop: 16,
    borderTop: "1px solid #e2e8f0"
  },
  fieldConfigHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 4
  },
  fieldConfigHeader: { fontSize: 13.5, fontWeight: 700, color: "#1e293b" },
  fieldConfigHint: { fontSize: 12, color: "#64748b", marginBottom: 12 },
  selectAllGroup: { display: "flex", gap: 8 },
  selectAllBtn: {
    padding: "4px 10px",
    borderRadius: 5,
    border: "1px solid #2563eb",
    background: "#fff",
    color: "#2563eb",
    fontSize: 12,
    fontWeight: 600
  },
  fieldGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: "8px 16px"
  },
  fieldGroupSection: {
    marginTop: 16,
    paddingTop: 14,
    borderTop: "1px dashed #e2e8f0"
  },
  fieldGroupHeader: {
    fontSize: 12.5,
    fontWeight: 700,
    color: "#475569",
    marginBottom: 10,
    textTransform: "uppercase",
    letterSpacing: 0.3
  },
  fieldCheckRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13.5,
    color: "#334155",
    cursor: "pointer"
  },
  listSection: { marginBottom: 20 },
  listTitle: { fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 10 },
  emptyState: {
    fontSize: 13,
    color: "#94a3b8",
    padding: "16px 0",
    borderTop: "1px solid #f1f5f9"
  },
  erpTable: { width: "100%", borderCollapse: "collapse", tableLayout: "fixed" },
  erpTh: {
    background: "#f8fafc",
    fontSize: 13,
    fontWeight: 600,
    padding: "10px 12px",
    borderBottom: "1px solid #e5e7eb",
    color: "#1e293b",
    textAlign: "left"
  },
  erpTd: { fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155", textAlign: "left" },
  dangerBtn: {
    padding: "4px 10px",
    borderRadius: 5,
    border: "1px solid #dc2626",
    background: "#fff",
    color: "#dc2626",
    cursor: "pointer",
    fontSize: 12,
    marginLeft: 8
  },
  linkBtn: {
    padding: "4px 10px",
    borderRadius: 5,
    border: "1px solid #2563eb",
    background: "#fff",
    color: "#2563eb",
    cursor: "pointer",
    fontSize: 12
  },
  removeBtn: {
    marginLeft: 8,
    border: "none",
    background: "transparent",
    color: "#ef4444",
    cursor: "pointer",
    fontSize: 12
  },
  primaryBtn: {
    marginTop: 4,
    padding: "12px 20px",
    background: "#2563eb",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    cursor: "pointer"
  },
  messageBox: {
    marginTop: 20,
    padding: 15,
    background: "#f1f5f9",
    borderRadius: 6,
    whiteSpace: "pre-line",
    border: "1px solid #cbd5e1"
  }
};

export default Role;